import { giphyLightGrey } from '@giphy/js-brand'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DroppableStateSnapshot,
    DropResult,
} from 'react-beautiful-dnd'
import styled, { css } from 'styled-components'
import { CreationToolUploadContext } from '../manager'
import { CreationToolUploadSource } from '../types'
import { CheckIcon, ErrorIcon } from './icons'
import Item from './item'

const Bucket = styled.div`
    margin-bottom: 30px;

    &:last-child {
        margin-bottom: 0;
    }
`

const DnDContainer = styled.div<{ isDim?: boolean; isDragging?: boolean } & any>`
    ${props =>
        props.isDim &&
        css`
            opacity: 0.75;
            pointer-events: none;
        `}
    ${props =>
        props.isDragging &&
        css`
            filter: drop-shadow(3px 3px 3px #000000);
        `}
`
const ListContainer = styled.div<{ isDragging: boolean }>`
    ${props =>
        !props.isDragging &&
        css`
            &:hover > ${DnDContainer} {
                opacity: 0.75;
            }
            &:hover > ${DnDContainer}:hover {
                opacity: 1;
            }
        `}
`

const Header = styled.div`
    align-items: center;
    color: ${giphyLightGrey};
    display: flex;
    font-size: 15px;
    font-weight: bold;
    height: 36px;
    line-height: 1;
    width: 100%;

    svg {
        display: block;
        height: 16px;
        margin-right: 8px;
    }

    span {
        color: #fff;
        display: inline-block;
        margin-left: 8px;
    }
`

const CreationUploadListBulk = () => {
    const { uploadSources } = useContext(CreationToolUploadContext)
    const numberOfSources = useRef(uploadSources.length)
    const [errorSources, setErrorSources] = useState<CreationToolUploadSource[]>([])
    const [readySources, setReadySources] = useState<CreationToolUploadSource[]>([])
    const [hidePreviewAutoExpand, setHidePreviewAutoExpand] = useState(false)

    // separate sources into ones with errors and ones without
    useEffect(() => {
        setErrorSources(uploadSources.filter(source => !!source.error))
        setReadySources(uploadSources.filter(source => !source.error))
        if (uploadSources.length !== numberOfSources.current) {
            // we only need to do this once when the sources change the first time
            setHidePreviewAutoExpand(true)
        }
    }, [uploadSources])

    const onDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination } = result
            // dropped outside the list
            if (!destination) {
                return
            }

            const { index: sourceIndex } = source
            const { index: destinationIndex } = destination
            if (sourceIndex === destinationIndex) {
                return
            }
            const newUploadSources = [...readySources]
            const moveIt = newUploadSources.splice(sourceIndex, 1)
            newUploadSources.splice(destinationIndex, 0, moveIt[0])
            setReadySources(newUploadSources)
        },
        [readySources]
    )

    return (
        <>
            <Bucket>
                {errorSources.length > 0 && (
                    <Header>
                        <CheckIcon />
                        Ready for Upload
                        <span>{readySources.length}</span>
                    </Header>
                )}
                <DragDropContext onDragEnd={onDragEnd} onDragStart={() => setHidePreviewAutoExpand(true)}>
                    <Droppable droppableId="upload">
                        {(provided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => {
                            return (
                                <ListContainer
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    isDragging={dropSnapshot.isDraggingOver}
                                >
                                    {readySources.map((source, i) => (
                                        <Draggable
                                            draggableId={source.id}
                                            key={source.id}
                                            index={i}
                                            isDragDisabled={readySources.length < 2}
                                        >
                                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                                <DnDContainer
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    ref={provided.innerRef}
                                                    key={source.id}
                                                    isDim={dropSnapshot.isDraggingOver && !snapshot.isDragging}
                                                    isDragging={snapshot.isDragging}
                                                >
                                                    <Item
                                                        source={source}
                                                        isPreviewingByDefault={
                                                            !hidePreviewAutoExpand && (i === 0 || source.isDuplicate)
                                                        }
                                                    />
                                                </DnDContainer>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </ListContainer>
                            )
                        }}
                    </Droppable>
                </DragDropContext>
            </Bucket>
            {errorSources.length > 0 && (
                <Bucket>
                    <Header>
                        <ErrorIcon />
                        Fix Errors to Upload
                        <span>{errorSources.length}</span>
                    </Header>
                    {errorSources.map(source => (
                        <Item key={source.id} source={source} />
                    ))}
                </Bucket>
            )}
        </>
    )
}

export default CreationUploadListBulk
